import { DELIVERY_SCHEDULE_COPY } from "@shared/orders/order-delivery-schedule";

export const FIELD_LABELS = {
  dateUnknown: "Date unknown",
  action: "Action",
  address: "Address",
  adminActions: "Admin Actions",
  amount: "Quantity",
  batchCreated: "Batch created",
  batchNumber: "Batch #",
  documentUploaded: "uploaded",
  documentReplaced: "replaced",
  carrier: "Carrier",
  casNumber: "CAS Number",
  casNumberShort: "CAS #",
  chemical: "Chemical",
  chemicalName: "Chemical Name",
  company: "Company",
  companyName: "Company Name",
  contact: "Contact",
  copyFromExistingOrder: "Copy From Existing Order",
  contactPerson: "Contact Person",
  country: "Country",
  currency: "Currency",
  contactType: "Type",
  currentAmount: "Current Qty",
  date: "Date",
  datePaid: "Date Paid",
  delete: "Delete",
  details: "Details",
  source: "Source",
  detailsView: "View Details",
  direction: "Direction",
  disposalReason: "Disposal Reason",
  email: "Email",
  existingBatch: "Existing Batch",
  expectedDeliveryDate: "Expected Delivery",
  externalBatchNumber: "External Batch Number",
  internalProductNumber: "Internal Product Number",
  externalProductNumber: "External Product Number",
  finalCountedAmount: "Final Counted Qty",
  initialAmount: "Initial Qty",
  initialStockType: "Record starting stock as",
  purchaseDate: "Purchase Date",
  internalBatchNumber: "Internal Batch Number",
  inventoryMovement: "Inventory Movement",
  inventoryEffect: "Inventory Effect",
  invoiceNumber: "Invoice #",
  lastLoginAt: "Last Logged In",
  lastOrderDate: "Last Order Date",
  lastModified: "Last Modified",
  location: "Location",
  history: "History",
  name: "Name",
  noHistory: "No history",
  notes: "Notes",
  orderedBy: "Ordered By",
  paidAmount: "Paid Amount",
  priceInTotal: "Price In Total",
  balanceDue: "Balance Due",
  totalPaid: "Total Paid",
  shippingHandlingDutyCharges: "S&H/Duty Charges",
  submittedBy: "Submitted By",
  trackingNumber: "Tracking Number",
  warehouseCharges: "Warehouse Charges",
  party: "Supplier/Purchaser",
  password: "Password",
  people: "People",
  phone: "Phone",
  phoneNumber: "Phone Number",
  productNumber: "Product #",
  priceRate: "$ / mass",
  purchaser: "Purchaser",
  quantity: "Qty Change",
  quantityShort: "Qty",
  quantityLeft: "Qty Left",
  quantityOrdered: "Qty Ordered",
  orderQuantity: "Quantity",
  record: "Record",
  referenceNumber: "Reference Number",
  referenceNumberShort: "Reference #",
  customerPoNumber: "Customer PO #",
  requestedBy: "Requested By",
  role: "Role",
  rowNumber: "#",
  splitFromBatch: "Split from batch",
  splitFrom: "Split From",
  status: "Status",
  supplier: "Supplier",
  supplierBatchNumber: "Supplier Batch #",
  totalPrice: "Total Price",
  transactionDate: "Transaction Date",
  transactionType: "Transaction Type",
  type: "Type",
  unit: "Unit",
  usageDetails: "Usage Details",
  user: "User",
} as const;

export function unresolvedEntityLabel(label: string, id: string | number): string {
  return `${label} #${id}`;
}

export const TABLE_CELL_LABELS = {
  loadFailed: "Unavailable",
  empty: "-",
} as const;

export const TRANSACTION_FIELD_LABELS = {
  ...FIELD_LABELS,
  batchNumber: "Internal Batch #",
  batchNumberLong: "Internal Batch Number",
  referenceNumber: "Internal PO #",
} as const;

export const INVENTORY_EFFECT_LABELS = {
  applied: "Applied",
  recordOnly: "Record only",
} as const;

export const PERMISSION_SUMMARY_LABELS = {
  manageInventory: "Manage Inventory",
  viewTransactionLedger: "View Transaction Ledger",
  viewCommercialDetails: "View Commercial Details",
  manageUsers: "Manage Users",
  accessAuditLog: "Access Audit Log",
} as const;

export const PERMISSIONS_DIALOG_LABELS = {
  title: "User Permissions",
  description: (name: string) => `Update role and permissions for ${name}.`,
  rolePlaceholder: "Select a role",
  summaryTitle: "Role Permissions",
  summaryHint: "Permissions are determined by the user's role:",
  granted: "Granted",
  notGranted: "Not Granted",
  cancel: "Cancel",
  save: "Save Permissions",
  saving: "Saving...",
} as const;

export const TOAST_TITLES = {
  success: "Success",
  error: "Error",
  passwordResetSent: "Password Reset Email Sent",
  passwordResetFailed: "Reset Failed",
  loginFailed: "Login failed",
  viewOnlyDemo: "Action unavailable in demo",
} as const;

export const DOCUMENT_MANAGER_LABELS = {
  versionHistoryTitle: (label: string | number) => `${label} Version History`,
  addDocumentRegion: "Add document",
  addDocumentMode: "How to add a document",
  uploadMode: "Upload",
  uploadedDocumentsRegion: "Uploaded documents",
  documentRecordSummary: "Document record summary",
  noDocumentsUploaded: "No documents uploaded yet.",
  noMatchingDocumentType: "No matching document type.",
  documentTypeCombobox: "Document type",
  selectDocumentTypePlaceholder: "Select type",
  searchDocumentTypesCombobox: "Search document types",
  searchDocumentTypesPlaceholder: "Search document types...",
  documentTypeSuggestions: "Document type suggestions",
  allTypesUploaded: "All types uploaded",
  genericDocument: "document",
  viewPdfTooltip: "View PDF",
  downloadPdfTooltip: "Download PDF",
  downloadMenuItem: "Download",
  replacePdfTooltip: "Replace PDF",
  replaceMenuItem: "Replace",
  uploadPdfTooltip: "Upload PDF",
  uploadingPdfTooltip: "Uploading PDF",
  uploading: "Uploading…",
  uploadedPrefix: "Uploaded",
  noPdfUploaded: "No PDF uploaded yet",
  versionHistoryTooltip: "Version history",
  versionHistoryMenuItem: "Version history",
  deletePdfTooltip: "Delete PDF",
  deleteMenuItem: "Delete",
  moreActionsTooltip: "More actions",
  replacementReason: "Replacement reason (optional)",
  previewNotSaved: "Not saved — UI preview",
} as const;

export function uploadedDocumentsHeading(count: number) {
  return `${DOCUMENT_MANAGER_LABELS.uploadedDocumentsRegion} (${count})`;
}
export function chooseDocumentPdfLabel(label?: string) {
  return `Choose ${label ?? DOCUMENT_MANAGER_LABELS.genericDocument} PDF`;
}
export function uploadDocumentPdfLabel(label?: string) {
  return `Upload ${label ?? DOCUMENT_MANAGER_LABELS.genericDocument} PDF`;
}
export function chooseAdditionalDocumentPdfLabel(label: string) {
  return `Choose another ${label} PDF`;
}
export function addAnotherDocumentLabel(label: string) {
  return `Add Another ${label}`;
}
export function chooseReplacementPdfLabel(label: string) {
  return `Choose replacement ${label} PDF`;
}
export function viewDocumentPdfLabel(label: string) {
  return `View ${label} PDF`;
}
export function downloadDocumentPdfLabel(label: string) {
  return `Download ${label} PDF`;
}
export function replaceDocumentPdfLabel(label: string) {
  return `Replace ${label} PDF`;
}
export function deleteDocumentPdfLabel(label: string) {
  return `Delete ${label} PDF`;
}
export function versionHistoryForLabel(label: string) {
  return `View version history for ${label}`;
}
export function moreActionsForLabel(label: string) {
  return `More actions for ${label}`;
}

export const BATCH_DOCUMENTS_DIALOG_LABELS = {
  title: "Batch Documents",
  description: "Upload and access documents for this inventory batch.",
  batchNumberFallback: "not set",
} as const;

export const ORDER_DOCUMENTS_DIALOG_LABELS = {
  title: "Order Documents",
  description: "Upload and access this order's quotes, correspondence, and billing documents.",
} as const;

export const ADDRESS_COPY = {
  addresses: "Addresses",
  contactAddressesHint: "Default billing is the bill-to or vendor address. Default shipping is the ship-to address.",
  addAddress: "Add address",
  editAddress: "Edit address",
  addressLabel: "Label",
  addressLines: "Address",
  country: "Country",
  defaultBilling: "Default billing",
  defaultShipping: "Default shipping",
  archive: "Archive",
  saveAddress: "Save address",
  noAddresses: "No addresses yet.",
  saved: "Address saved",
  saveFailed: "The address was not saved",
  useDefault: "Company default",
  billToAddress: "Bill-to address",
  vendorAddress: "Vendor address",
  shipToAddress: "Ship-to address",
  receivingAddress: "Receiving address",
} as const;

export const CONTACT_ADDRESS_LABELS = { purchase: ADDRESS_COPY.vendorAddress, sale: ADDRESS_COPY.billToAddress } as const;
export const MOVEMENT_ADDRESS_LABELS = { purchase: ADDRESS_COPY.receivingAddress, sale: ADDRESS_COPY.shipToAddress } as const;

export const COMPANY_SETTINGS_COPY = {
  usersTab: "Users",
  companyTab: "Company",
  adminTabs: "Admin sections",
  profile: "Profile",
  profileHint: "Printed on every generated document.",
  legalName: "Company name",
  initialism: "Initialism",
  initialismHint: "Starts document numbers.",
  paymentTermsDays: "Payment terms (days)",
  contactLines: "Phone, email, website",
  saveProfile: "Save profile",
  addresses: "Addresses",
  addressesHint: "Default billing is the sender address. Default shipping receives purchases and ships releases.",
  branding: "Logo and stamps",
  brandingHint: "PNG or JPEG. A replaced or removed image never changes saved documents.",
  logo: "Logo",
  uploadLogo: "Upload logo",
  replace: "Replace",
  stamps: "Stamps",
  stampLabel: "Stamp name",
  addStamp: "Add stamp",
  deactivate: "Remove",
  noStamps: "No stamps yet.",
  versionHistory: "Version history",
  markVersionsHint: "Every image ever uploaded, including replaced ones. A saved document keeps the version it used.",
  profileFirst: "Save the profile first.",
  documentDefaults: "Document defaults",
  documentDefaultsHint: "Standard text for values no order records, per document type.",
  footer: "Footer",
  saveDefaults: "Save defaults",
  saved: "Saved",
  saveFailed: "Not saved",
} as const;

export const DOCUMENT_AUDIT_EVENT_LABELS = {
  mark_uploaded: "Mark Uploaded",
  mark_replaced: "Mark Replaced",
  mark_deactivated: "Mark Removed",
  company_updated: "Company Settings Updated",
  address_saved: "Company Address Saved",
  address_archived: "Company Address Archived",
  action_denied: "Document Action Denied",
  action_failed: "Document Action Failed",
  companySettings: "Company settings",
} as const;

export const DOCUMENT_GENERATION_COPY = {
  open: "Generate document",
  generateMode: "Generate",
  title: "Generate Document",
  reviseTitle: "Revise Document",
  revise: "Revise",
  revisionReason: "Reason for this revision",
  description: "Built from this order and its records. Fix any missing value in its record, then save.",
  documentType: "Document type",
  source: "Based on",
  noSource: "No shipment (enter quantities)",
  recordedShipments: "Recorded shipments",
  plannedDeliveries: "Planned deliveries",
  noDate: "(no date)",
  plannedLabel: (date: string, quantity: string) => `Planned ${date} — ${quantity}`,
  missing: "Missing from records",
  nothingMissing: "Everything comes from this order and its records.",
  alreadyExists: (title: string, number: string) => `This order already has this ${title.toLowerCase()} (${number}). Use Revise to change it.`,
  rendering: "Updating preview…",
  previewIncomplete: "Blanks are missing values. Fix them in their records to save.",
  factSources: {
    order: "Edit the order",
    contact: "Update the contact in Contacts",
    shipment: "Record or edit the shipment",
    company: "Update Admin → Company",
  },
  previewFailed: "The preview could not be updated.",
  save: "Save to Documents",
  saving: "Saving…",
  previewTitle: "Preview",
  marks: "Marks",
  signAs: (name: string) => `Sign as ${name}`,
  stamp: "Stamp",
  noStamp: "No stamp",
  noStampHint: "No stamp yet. An admin can add one in Admin → Company.",
  saveBlocked: (count: number) => (count === 1 ? "Save is off until 1 missing value is fixed." : `Save is off until ${count} missing values are fixed.`),
  saved: "Document saved",
  savedDescription: "The PDF is now in this order's Documents.",
  failed: "Document not saved",
  loadFailed: "Could not load the draft",
  shipmentLabel: (date: string, carrier: string | null) => (carrier ? `${date} · ${carrier}` : date),
  sectionField: (section: string, field: string) => `${section} · ${field}`,
  lineField: (line: number, field: string) => `Line ${line} · ${field}`,
  chargeField: (charge: number, field: string) => `Charge ${charge} · ${field}`,
} as const;

export const ORDER_VIEW_SELECT_LABELS = {
  trigger: "Orders view",
  search: "Search views",
  searchPlaceholder: "Search views...",
  options: "Orders views",
  empty: "No views match",
} as const;

export const ORDERS_CSV_LABELS = {
  button: "Download CSV",
  empty: "No orders match this view. Nothing was downloaded.",
  loadFailed: "Orders could not load. Nothing was downloaded.",
  writeFailed: "The CSV file could not be created.",
  downloaded: (count: number) => `Downloaded ${count} ${count === 1 ? "order" : "orders"}.`,
} as const;

export const ORDER_OPERATIONAL_LEDGER_LABELS = {
  payments: "Payments",
  receipts: "Receipts",
  shipments: "Shipments",
  moreThanOrdered: "more than ordered",
  invoiceNumber: "Invoice Number",
  invoiceDate: "Invoice Date",
  addBatchLine: "Add Batch Line",
  edit: "Edit",
  delete: "Delete",
  cancel: "Cancel",
  saving: "Saving...",
  batchNumberShort: "Batch #",
} as const;

export const STATIC_DEMO_MESSAGES = {
  demoBadge: "Demo",
  bannerText: "Sample data. Changes are temporary and reset when you refresh.",
  writeBlockedDescription: "This external action is not available in the temporary demo.",
  recordLimitReached: "Temporary demo record limit reached. Refresh the page to reset the demo.",
  noFixtureData: (path: string) => `No demo fixture data for ${path}`,
  temporaryChangeTitle: "Temporary demo change",
  temporaryChangeDescription: "This change exists only in this browser session and resets when the page refreshes.",
  landingPageUrl: "https://cheminventory.co",
  landingPageLinkText: "← ChemInventory",
  bookAMeetingUrl: "https://cal.com/shaun-porwal-junto/30min",
  bookAMeetingLinkText: "Book a meeting",
  contactUsUrl: "mailto:shaun.porwal@juntotechnologies.com?subject=CIMS%20Query",
  contactUsLinkText: "Contact us",
} as const;

export function buildMutationErrorToast(error: Error, fallbackTitle: string) {
  if (error.message === STATIC_DEMO_MESSAGES.writeBlockedDescription) {
    return { title: TOAST_TITLES.viewOnlyDemo, description: error.message };
  }
  if (isRuntimeFault(error)) {
    console.error(UNEXPECTED_ERROR_COPY.logPrefix, error);
    return { title: fallbackTitle, description: UNEXPECTED_ERROR_COPY.description };
  }
  return { title: fallbackTitle, description: error.message };
}

const RUNTIME_FAULTS = [TypeError, ReferenceError, RangeError, SyntaxError, EvalError, URIError];

function isRuntimeFault(error: Error): boolean {
  return RUNTIME_FAULTS.some((fault) => error instanceof fault);
}

export const UNEXPECTED_ERROR_COPY = {
  description: "Nothing was saved. The page hit a problem or lost its connection. Reload the page and try again.",
  logPrefix: "[unexpected client error]",
} as const;


export const INITIAL_STOCK_UI = {
  createDescription: "Add an inventory record and record any starting stock in one save.",
  stockCreated: "Chemical created with starting stock recorded.",
} as const;

export const CHEMICAL_FORM_UI = {
  startingStockHeading: "Starting stock",
  batchNumberHintLabel: "About Batch #",
  batchNumberHint: "Generated automatically for new batches. You can replace it with a custom value.",
} as const;

export const CONTACT_FORM_PLACEHOLDERS = {
  company: "Enter company name",
  contactType: "Select a type",
  address: "Enter address",
  country: "Optional",
  notes: "Any additional information",
} as const;

export const FORM_VALIDATION_MESSAGES = {
  quantityPositive: "Quantity must be greater than 0",
  deliveryLineRequired: "Add at least one delivery line",
} as const;

export const ORDER_DELIVERY_LABELS = {
  schedule: "Delivery Schedule",
  guidance: "Include quantities already shipped or received. Dates are optional; totals update automatically.",
  date: "Delivery Date",
  quantity: "Quantity Due",
  rowDate: (row: number) => `Delivery ${row} date`,
  rowQuantity: (row: number) => `Delivery ${row} quantity`,
  rowCarrier: (row: number) => `Delivery ${row} carrier`,
  rowBatch: (row: number) => `Delivery ${row} batch #`,
  rowShipTo: (row: number) => `Delivery ${row} ship-to`,
  columnQuantity: (unit: string) => (unit ? `${FIELD_LABELS.quantityShort} (${unit})` : FIELD_LABELS.quantityShort),
  columnDate: "Date",
  columnCarrier: "Carrier",
  columnBatch: "Batch #",
  columnShipTo: "Ship-to",
  detailSeparator: " · ",
  removeRow: (row: number) => `Remove delivery ${row}`,
  add: "Add delivery",
  overdue: "Overdue",
  none: "No delivery dates",
  notScheduled: DELIVERY_SCHEDULE_COPY.notScheduled,
  left: (left: string, total: string, unit: string) => `${left} of ${total} ${unit} left`,
  amount: (quantity: string, unit: string) => `${quantity} ${unit}`,
  next: (date: string, amount: string) => `${date} · ${amount}`,
  nextWithMore: (next: string, laterCount: number) => `${next} (+${laterCount} more)`,
  total: (quantity: string, unit: string) => `Total: ${quantity} ${unit}`,
  csvEntry: (date: string, left: string) => `${date}: ${left}`,
  csvSeparator: "; ",
} as const;

export const DRAFT_LABELS = {
  discardDraft: "Discard Draft",
} as const;

export const CHEMICAL_SUGGESTION_UI = {
  listLabel: "Matching chemicals",
} as const;

export const DASHBOARD_LABELS = {
  areaTrigger: "Dashboard area",
  areaSearch: "Search areas",
  areaSearchPlaceholder: "Search areas...",
  areaEmpty: "No areas match",
  periodTrigger: "Period",
  periodSearch: "Search periods",
  periodSearchPlaceholder: "Search periods...",
  periodEmpty: "No periods match",
  customFrom: "From",
  customTo: "To",
  noAreas: "No Dashboard areas are available for your role.",
  snapshot: "Current snapshot",
  incomplete: "Incomplete",
  loading: "Loading…",
  unavailable: "Unavailable",
  unavailableDetail: "This figure could not load.",
  empty: "No data for this period.",
  noValues: "No values can be shown for this period.",
  summarySuffix: "summary",
  definition: "How this is counted",
  other: (count: number) => `Other (${count})`,
  showAllValues: "Show all values",
  valueColumn: "Value",
  tilesRegion: "Key figures",
  chartsRegion: "Breakdowns",
  areaAction: (area: string, action: string) => `${area} ${action.toLowerCase()}`,
  viewMissingPrices: "View all orders missing price",
  yearDirection: (year: string, direction: string) => `${year} ${direction}`,
} as const;

export const ORDER_PRICE_FILTER_COPY = {
  invalid: "This filter link is invalid. Select an Orders view to show orders.",
  denied: "This filter needs financial access. Select an Orders view to show orders.",
  unavailable: "This filter is unavailable. Select an Orders view to show orders.",
  loadFailed: "Orders could not load. Try again.",
} as const;

export const CONTACT_PEOPLE_DIALOG_COPY = {
  personAddedSuccessfully: "Person added successfully",
  personUpdatedSuccessfully: "Person updated successfully",
  personRemovedSuccessfully: "Person removed successfully",
  manageThePeopleAssociatedWithThis: "Manage the people associated with this organization.",
  loadingPeople: "Loading people...",
  noPeopleAddedYet: "No people added yet.",
  addPerson: "Add Person",
} as const;

export const CONTACT_ROW_ACTIONS_COPY = {
  managePeopleFor: (company: string | number) => `Manage people for ${company}`,
  editFor: (company: string | number) => `Edit ${company}`,
  deleteFor: (company: string | number) => `Delete ${company}`,
} as const;

export const DEVELOPMENT_INDICATOR_COPY = {
  developmentEnvironment: "Development Environment",
} as const;

export const FILTER_POPOVER_COPY = {
  filterOptions: "Filter Options",
} as const;

export const HEADER_COPY = {
  manageYourChemicalInventoryEfficiently: "Manage your chemical inventory efficiently",
} as const;

export const CONTACT_FORM_DIALOG_COPY = {
  editCompany: "Edit Company",
  addNewCompany: "Add New Company",
  updateTheCompanyInformationBelow: "Update the company information below.",
  enterTheDetailsOfTheNew: "Enter the details of the new company.",
} as const;

export const BATCH_DOCUMENT_VERSIONS_DIALOG_COPY = {
  everyVersionEverUploadedIncludingVersions: "Every version ever uploaded, including versions superseded by a later upload or a cleared current slot.",
} as const;

export const BATCH_SPLIT_DIALOG_COPY = {
  splitBatch: "Split Batch",
  addChildBatch: "Add child batch",
  totalSplit: "Total split",
  totalExceedsAvailableQuantityBy: "Total exceeds available quantity by",
} as const;

export const CHEMICAL_FORM_SECTIONS_COPY = {
  enterProductNumber: "Enter product number",
  batchNumber: "Batch number",
  eGSodiumHydroxide: "e.g. Sodium Hydroxide",
  eGLab1ShelfA: "e.g. Lab 1, Shelf A",
  enterInitialAmount: "Enter initial amount",
  enterSupplierBatchNumber: "Enter supplier batch number",
  enterInternalPoNumber: "Enter internal PO number",
} as const;

export const CHEMICAL_FORM_COPY = {
  editChemicalMetadataHereRecordStock: "Edit chemical metadata here. Record stock changes from inventory actions.",
  saveChemical: "Save Chemical",
} as const;

export const CHEMICAL_HISTORY_DIALOG_COPY = {
  chemicalHistory: "Chemical History",
  noBatchNumber: "No batch number",
} as const;

export const ORDER_EDITOR_COPY = {
  materialAndDeliveries: "Material & deliveries",
  casPlaceholder: "e.g. 1310-73-2",
  pricePlaceholder: "0.00",
} as const;

export const ORDER_AMEND_DIALOG_COPY = {
  editOrder: "Edit Order",
  correctTheUserEnteredDetailsOn: "Correct the user-entered details on this requested or placed order. Every saved change is recorded.",
  saveChanges: "Save Changes",
} as const;

export const ORDER_COPY_SELECTOR_COPY = {
  prefillsAnEditableDraft: "Prefills an editable draft.",
  searchExistingOrders: "Search existing orders",
  noExistingOrders: "No existing orders",
  noMatchingOrders: "No matching orders",
} as const;

export const ORDER_CREDIT_MEMO_DIALOG_COPY = {
  deleteCreditMemoOfOn: (value: string | number, date: string | number) => `Delete credit memo of ${value} on ${date}`,
  creditMemos: "Credit Memos",
  recordCreditMemosIssuedOnThis: "Record credit memos issued on this returned order. Supports more than one.",
  creditMemo: "Credit Memo",
  noCreditMemosRecordedYet: "No credit memos recorded yet.",
  addCreditMemo: "Add Credit Memo",
  saveCreditMemo: "Save Credit Memo",
} as const;

export const ORDER_DETAIL_DIALOG_COPY = {
  markReturned: "Mark Returned",
  cancellationReason: "Cancellation Reason:",
  returnNotes: "Return Notes:",
} as const;


export const ORDER_FORM_DIALOG_COPY = {
  addOrder: "Add Order",
  addACurrentOrHistoricalPurchase: "Add a current or historical purchase or sale using its actual status and event dates.",
} as const;

export const ORDER_HISTORY_DIALOG_COPY = {
  orderHistory: "Order History",
} as const;

export const ORDER_PAYMENT_DIALOG_COPY = {
  deletePaymentOfOn: (value: string | number, paidDate: string | number) => `Delete payment of ${value} on ${paidDate}`,
  recordWhatSBeenPaidOn: "Record what's been paid on this order, independent of its fulfillment status. Supports multiple installments.",
  paidSoFar: "Paid So Far",
  noPaymentsRecordedYet: "No payments recorded yet.",
  addPayment: "Add Payment",
  savePayment: "Save Payment",
} as const;

export const ORDER_REQUESTOR_FIELD_COPY = {
  selectCustomerFirst: "Select customer first",
  noContactPeopleAreListedFor: "No contact people are listed for this company. Keep the company as the requestor, or add a person in Contacts.",
} as const;

export const COMPACT_DOCUMENT_MANAGER_COPY = {
  confirmReplacement: "Confirm Replacement",
  replaceWith: (label: string | number, name: string | number) => `Replace ${label} with ${name}?`,
} as const;

export const DELETE_CONFIRMATION_COPY = {
  confirmDeletion: "Confirm Deletion",
  areYouSureYouWantTo: (value: string | number) => `Are you sure you want to delete ${value}? This action cannot be undone.`,
} as const;

export const DOCUMENT_VERSION_LIST_COPY = {
  viewVersionFrom: (label: string | number, timestamp: string | number) => `View ${label} version from ${timestamp}`,
  permanentlyDeleteVersionFrom: (label: string | number, timestamp: string | number) => `Permanently delete ${label} version from ${timestamp}`,
  deletePermanently: "Delete permanently",
  loadingVersions: "Loading versions…",
  noVersionsUploadedYet: "No versions uploaded yet",
} as const;

export const HISTORY_CARD_LIST_COPY = {
  loadingHistory: "Loading history...",
} as const;

export const HISTORY_LIST_COPY = {
  noHistoryAvailable: "No history available",
} as const;

export const TRANSACTION_AMEND_DIALOG_COPY = {
  editTransactionDetails: "Edit Transaction Details",
  noCompanyApplies: "No company applies",
  enterTotalPrice: "Enter total price",
} as const;

export const TRANSACTION_FORM_DIALOG_COPY = {
  editInventoryTransaction: "Edit Inventory Transaction",
  recordInventoryTransaction: "Record Inventory Transaction",
  recordTransaction: "Record Transaction",
} as const;

export const TRANSACTION_FORM_FIELDS_COPY = {
  selectATransactionType: "Select a transaction type",
  noMatchingBatches: "No matching batches",
  enterQuantity: "Enter quantity",
  enterFinalCountedAmount: "Enter final counted amount",
} as const;

export const TRANSACTION_HISTORY_DIALOG_COPY = {
  transactionDetails: "Transaction Details",
  modificationHistory: "Modification History",
} as const;

export const ADD_USER_DIALOG_COPY = {
  fullName: "Full Name",
  eGJohnDoe: "e.g. John Doe",
  pleaseFillInAllRequiredFields: "Please fill in all required fields",
  validationError: "Validation Error",
  addNewUser: "Add New User",
  createANewUserAccountWith: "Create a new user account with appropriate permissions.",
  addUser: "Add User",
} as const;

export const ORDER_HISTORY_LIST_COPY = {
  importedFromACsvFileBy: "Imported from a CSV file by",
  noHistoryYet: "No history yet",
} as const;

export const MOVEMENTS_PREVIEW_PANEL_COPY = {
  deliveryAllocation: "Recorded quantities reduce the earliest scheduled delivery first. You do not need to select a delivery date.",
  loading: (plural: string) => `Loading ${plural.toLowerCase()}...`,
  invalidCharge: "Enter a valid charge amount.",
  invalidQuantity: "Enter a quantity greater than zero.",
  add: (singular: string) => `Add ${singular}`,
  addHeading: (singular: string) => `Add ${singular.toLowerCase()}`,
  recordHeading: (singular: string, quantity: string, unit: string, date: string) => `${singular} · ${quantity} ${unit} · ${date}`,
  editHeading: (summary: string) => `Edit ${summary.charAt(0).toLowerCase()}${summary.slice(1)}`,
  unavailable: (singular: string) => `This ${singular.toLowerCase()} is no longer available. Cancel to return to the list.`,
  date: (singular: string) => `${singular} Date`,
  notes: (singular: string) => `${singular} Notes`,
  quantity: (singular: string, index: number) => `${singular} Quantity ${index}`,
  internalBatchLabel: (index: number) => `${FIELD_LABELS.internalBatchNumber} ${index}`,
  quantityHeader: (unit: string) => `${FIELD_LABELS.quantityShort} (${unit})`,
  internalBatchShort: `Internal ${ORDER_OPERATIONAL_LEDGER_LABELS.batchNumberShort}`,
  externalBatchShort: (qualifier: string) => `${qualifier} ${ORDER_OPERATIONAL_LEDGER_LABELS.batchNumberShort}`,
  savedLine: (quantity: string, unit: string, internal: string | null, external: string | null, qualifier: string) =>
    `${quantity} ${unit}${internal ? ` · Internal ${internal}` : ""}${external ? ` · ${qualifier} ${external}` : ""}`,
  totals: (past: string, quantity: string, total: string, unit: string) => `${past} ${quantity} ${unit} of ${total} ${unit}`,
  save: (singular: string) => `Save ${singular}`,
  batchNumber: (externalQualifier: string | number, value: string | number) => `${externalQualifier} Batch Number ${value}`,
  removeLine: (value: string | number) => `Remove Line ${value}`,
  saveChanges: (singular: string | number) => `Save ${singular} Changes`,
  savePreview: (singular: string | number) => `Save Preview ${singular}`,
} as const;

export const PERSISTED_PAYMENTS_PANEL_COPY = {
  paidSoFar: "Paid so far:",
  savePaymentChanges: "Save Payment Changes",
} as const;

export const ORDER_ACTIONS_MENU_COPY = {
  orderActions: "Order Actions",
  placeTheOrderFirst: "Place the order first",
} as const;

export const ORDER_IMPORT_RESOURCES_DIALOG_COPY = {
  noValuesAvailable: "No values available",
  columnRequirements: "Column Requirements",
  preserveTheExactHeadersAndTheir: "Preserve the exact headers and their order. Use YYYY-MM-DD dates, format CAS and reference numbers as Text, use plain decimals without currency symbols or commas, keep one Order per row, and export as CSV UTF-8.",
  requiredColumns: "Required columns",
  optionalColumns: "Optional columns",
  referenceValues: "Reference values",
  supplierNames: "Supplier names",
  correctionsRequired: "Corrections required",
  correctTheExcelSourceExportCsv: "Correct the Excel source, export CSV UTF-8 again, and re-upload the complete file.",
  applyImport: "Apply Import",
  previewOnlyNothingHasBeenSaved: "Preview only—nothing has been saved yet.",
  enteredOn: "Entered On",
  thisCreates: "This creates",
  fromThisFileAndCannotBe: "from this file and cannot be undone. Editing and re-exporting this file afterward creates a new file, which can be imported again as new Orders — it will not detect or merge with the Orders already created here.",
  theFileIsNotValidUtf: "The file is not valid UTF-8. Export it from Excel as CSV UTF-8 and try again.",
  importOrders: "Import Orders",
  uploadAndValidatePurchaseAndSale: "Upload and validate Purchase and Sale Orders from one CSV file. Previewing does not save anything.",
  ordersCsv: "Orders CSV",
  chooseCsvFile: "Choose CSV File",
  noFileChosen: "No file chosen",
  couldNotReadThisCsv: "Could not read this CSV",
  validateAndPreview: "Validate and Preview",
  blankTemplate: "Blank Template",
  exampleCsv: "Example CSV",
  useTheExact: "Use the exact",
  templateYyyyMmDdDatesPlain: "template, YYYY-MM-DD dates, plain decimals, text-formatted CAS/reference values, and export from Excel as CSV UTF-8.",
} as const;

export const ORDERS_DIALOG_STACK_COPY = {
  markThisSaleAsInitiatedThis: "Mark this sale as initiated. This stamps you as the person who initiated it.",
  markThisOrderAsPlacedWith: "Mark this order as placed with the vendor. This stamps you as the person who ordered it.",
  cancelOrder: "Cancel Order",
  thisOrderWillNoLongerProceed: "This order will no longer proceed. This cannot be undone.",
  reasonOptional: "Reason (optional)",
  whyIsThisOrderBeingCancelled: "Why is this order being cancelled?",
  confirmsTheMaterialHasBeenPhysically: "Confirms the material has been physically received back. Recording a credit memo is only available after this.",
  notesOptional: "Notes (optional)",
  whatCameBackConditionEtc: "What came back, condition, etc.",
} as const;

export const ORDERS_TABLE_COPY = {
  orderDetails: "Order Details",
} as const;

export const PERSON_FORM_COPY = {
  enterName: "Enter name",
  eGSalesRep: "e.g. Sales Rep",
  enterEmail: "Enter email",
  enterPhoneNumber: "Enter phone number",
} as const;

export const TABLE_ROW_ACTIONS_COPY = {
  recordTransaction: "Record transaction",
  editDetails: "Edit details",
  splitBatch: "Split batch",
  otherActions: "Other actions",
} as const;

export const DATA_TABLE_COPY = {
  noResults: "No results.",
  actions: "Actions",
} as const;

export const DATE_PICKER_COPY = {
  pickADate: "Pick a date",
} as const;

export const PAGINATION_COPY = {
  goToPreviousPage: "Go to previous page",
  goToNextPage: "Go to next page",
  morePages: "More pages",
} as const;

export const SEARCHABLE_PICKER_COPY = {
  noAvailable: (listName: string | number) => `No ${listName} available`,
  search: (listName: string | number) => `Search ${listName}...`,
  noMatching: (listName: string | number) => `No matching ${listName}`,
} as const;

export const SIDEBAR_COPY = {
  toggleSidebar: "Toggle Sidebar",
} as const;

export const USE_AUTH_COPY = {
  loginResponseDidNotIncludeA: "Login response did not include a user profile",
  loginSuccessful: "Login successful",
  anUnknownErrorOccurred: "An unknown error occurred",
  registrationSuccessful: "Registration successful",
  registrationFailed: "Registration failed",
  loggedOutSuccessfully: "Logged out successfully",
  loggingOut: "Logging out...",
  youWillBeRedirectedToThe: "You will be redirected to the login page.",
} as const;

export const USE_BATCH_DOCUMENTS_COPY = {
  documentUploaded: "Document uploaded",
  thePdfHasBeenSavedFor: "The PDF has been saved for this batch.",
  documentRemoved: "Document removed",
  theCurrentDocumentHasBeenCleared: "The current document has been cleared.",
} as const;

export const USE_CHEMICAL_MUTATIONS_COPY = {
  failedTo: (action: string | number) => `Failed to ${action}`,
  chemicalAdded: "Chemical added",
  chemicalCreatedAt0UseTransact: "Chemical created at 0. Use Transact to add stock.",
  chemicalUpdated: "Chemical updated",
  theChemicalHasBeenUpdatedSuccessfully: "The chemical has been updated successfully",
  chemicalDeleted: "Chemical deleted",
  theChemicalHasBeenDeletedSuccessfully: "The chemical has been deleted successfully",
  transactionRecorded: "Transaction recorded",
  theInventoryTransactionHasBeenRecorded: "The inventory transaction has been recorded successfully",
  batchSplit: "Batch split",
  childBatchesHaveBeenCreatedSuccessfully: "Child batches have been created successfully.",
} as const;

export const USE_DOCUMENT_SLOTS_COPY = {
  uploadFailed: "Upload failed",
  deleteFailed: "Delete failed",
  versionDeleted: "Version deleted",
  theVersionHasBeenPermanentlyRemoved: "The version has been permanently removed.",
} as const;

export const USE_ORDER_DOCUMENTS_COPY = {
  thePdfHasBeenSavedFor: "The PDF has been saved for this order.",
} as const;

export const USE_ORDER_IMPORT_REFERENCES_COPY = {
  ordersImported: "Orders imported",
  orderWas: "Order was",
  ordersWere: "Orders were",
  importFailed: "Import failed",
} as const;

export const USE_ORDER_OPERATIONAL_LEDGERS_COPY = {
  orderUpdateFailed: "Order update failed",
} as const;

export const USE_ORDERS_COPY = {
  orderAdded: "Order added",
  theOrderAndItsLifecycleHistory: "The order and its lifecycle history were saved.",
  addOrder: "Add order",
  orderUpdated: "Order updated",
  theOrderHasBeenUpdated: "The order has been updated.",
  editOrder: "Edit order",
  orderPlaced: "Order placed",
  theOrderHasBeenMarkedAs: "The order has been marked as ordered.",
  placeOrder: "Place order",
  orderCancelled: "Order cancelled",
  theOrderHasBeenCancelled: "The order has been cancelled.",
  cancelOrder: "Cancel order",
} as const;

export const USE_TRANSACTIONS_COPY = {
  unknownId: (id: string | number) => `Unknown (ID: ${id})`,
  transactionRecordedSuccessfully: "Transaction recorded successfully",
  transactionDetailsUpdated: "Transaction details updated",
} as const;

export const USE_USERS_COPY = {
  userAddedSuccessfully: "User added successfully",
  failedToAddUser: "Failed to add user",
  permissionsForHaveBeenUpdatedSuccessfully: (name: string | number) => `Permissions for ${name} have been updated successfully.`,
  failedToUpdatePermissions: "Failed to update permissions",
  userDeletedSuccessfully: "User deleted successfully",
  failedToDeleteUser: "Failed to delete user",
} as const;

export const AUDIT_UTILS_COPY = {
  added: "Added",
  removed: "Removed",
  batchSplit: "Batch Split",
  allSources: "All Sources",
  orderAmended: "Order amended",
  createdFromBatchSplitFrom: (value: string | number) => `Created from batch split (from: ${value})`,
  initialInventoryEntry: "Initial inventory entry",
  chemicalRemovedFromInventory: "Chemical removed from inventory",
  noAmountChange: "No amount change",
  sourceDocuments: "Documents",
  sourceTransactions: "Transactions",
  sourceInventory: "Inventory",
  sourceOrders: "Orders",
  unknownValue: "unknown",
  documentFallback: "Document",
  documentUploaded: (document: string, file: string) => `${document} uploaded${file}`,
  documentReplaced: (document: string, file: string) => `${document} replaced${file}`,
  documentDeleted: (document: string) => `${document} deleted`,
  fileNameSuffix: (fileName: string) => `: ${fileName}`,
  orderEvent: (event: string) => `Order ${event.toLowerCase()}`,
  eventFields: (prefix: string, fields: string) => `${prefix}: ${fields}`,
} as const;

export const CONSTANTS_COPY = {
  lab1: "Lab 1",
  lab2: "Lab 2",
  supplierPurchaser: "Supplier & Purchaser",
} as const;

export const CONTACT_UTILS_COPY = {
  noPeopleAdded: "No people added",
  noAddress: "-",
} as const;

export const FORM_SCHEMAS_COPY = {
  onlyLettersNumbersAndDashesAre: "Only letters, numbers, and dashes are allowed",
  casNumberIsRequired: "CAS Number is required",
  invalidEmailAddress: "Invalid email address",
  passwordIsRequired: "Password is required",
  companyNameIsRequired: "Company name is required",
  nameIsRequired: "Name is required",
  invalidEmailFormat: "Invalid email format",
  chemicalIsRequired: "Chemical is required",
  unitIsRequired: "Unit is required",
  finalCountedAmountIsRequired: "Final counted amount is required",
  mustBeGreaterThan0: "Must be greater than 0",
  atLeastOneChildBatchIs: "At least one child batch is required",
  productNumberIsRequired: "Product Number is required",
  chemicalNameIsRequired: "Chemical name is required",
  locationIsRequired: "Location is required",
  initialAmountCannotBeNegative: "Initial amount cannot be negative",
  requestedDateIsRequired: "Requested date is required",
  requestedByIsRequired: "Requested By is required",
  customerRequestorIsRequired: "Customer Requestor is required",
  enterAChemicalNameOrCas: "Enter a Chemical Name or CAS Number",
  contactIsRequired: "Contact is required",
  chemicalNameOrCasNumberIs: "Chemical Name or CAS Number is required",
  requestorIsRequired: "Requestor is required",
} as const;

export const ORDER_ADAPTER_COPY = {
  unknownContact: "Unknown contact",
} as const;

export const ORDER_COPY_OPTIONS_COPY = {
  internalProduct: (internalProductNumber: string | number) => `Internal Product ${internalProductNumber}`,
  externalProduct: (externalProductNumber: string | number) => `External Product ${externalProductNumber}`,
} as const;

export const ORDER_DISPLAY_COPY = {
  purchaseRequested: "Purchase Requested",
  orderPlaced: "Order Placed",
  requestedOn: "Requested On",
  orderPlacedOn: "Order Placed On",
  dateReceived: "Date Received",
  placeOrder: "Place Order",
  markFulfilledArrived: "Mark Fulfilled / Arrived",
  incomingShipments: "Incoming Shipments",
  totalReceived: "Total Received",
  customerRequestReceived: "Customer Request Received",
  saleConfirmed: "Sale Confirmed",
  requestReceivedOn: "Request Received On",
  saleConfirmedOn: "Sale Confirmed On",
  dateShipped: "Date Shipped",
  customerRequestor: "Customer Requestor",
  initiateSale: "Initiate Sale",
  markFulfilledShipped: "Mark Fulfilled / Shipped",
  outgoingShipments: "Outgoing Shipments",
  totalShipped: "Total Shipped",
  paymentRecorded: "Payment Recorded",
  paymentAmended: "Payment Amended",
  paymentDeleted: "Payment Deleted",
  invoiceRecorded: "Invoice Recorded",
  invoiceAmended: "Invoice Amended",
  invoiceDeleted: "Invoice Deleted",
  receiptRecorded: "Receipt Recorded",
  shipmentRecorded: "Shipment Recorded",
  receiptShipmentAmended: "Receipt/Shipment Amended",
  receiptShipmentDeleted: "Receipt/Shipment Deleted",
  documentUploaded: "Document Uploaded",
  documentReplaced: "Document Replaced",
  documentDeleted: "Document Deleted",
  creditMemoRecorded: "Credit Memo Recorded",
  creditMemoDeleted: "Credit Memo Deleted",
  documentPreviewed: "Document Previewed",
  documentMarkApplied: "Mark Applied",
  documentGenerated: "Document Generated",
  documentCorrected: "Document Revised",
  documentViewed: "Document Link Issued",
  documentDownloadRequested: "Download Link Issued",
  recordedAt: "Recorded At",
  pricePerUnitUnit: "Price Per Unit ($/unit)",
} as const;

export const ORDER_DOCUMENT_CONTROLLER_COPY = {
  documentType: "Document Type",
} as const;

export const ORDER_LIFECYCLE_CONTROLLER_COPY = {
  previousStatus: "Previous Status",
  reason: "Reason",
} as const;

export const PROTECTED_ROUTE_COPY = {
  accessDenied: "Access Denied",
  youNeedAdministratorPrivilegesToAccess: "You need administrator privileges to access this page.",
} as const;

export const QUERYCLIENT_COPY = {
  serverDidNotAnswer: "The server could not finish this request. Try again in a minute. If it keeps happening, contact your administrator.",
} as const;

export const TRANSACTION_FORM_CONFIG_COPY = {
  enterPoNumber: "Enter PO number",
  enterSoNumber: "Enter SO number",
  enterDetailsAboutHowTheChemical: "Enter details about how the chemical was used",
  enterReasonForDisposal: "Enter reason for disposal",
  enterReferenceNumber: "Enter reference number",
} as const;

export const TRANSACTION_UTILS_COPY = {
  finalCount: (finalCountedAmount: string | number, unit: string | number) => `Final count: ${finalCountedAmount} ${unit}`,
} as const;

export const UTILS_COPY = {
  userNumber: (id: string | number) => `User #${id}`,
  unknownUser: "Unknown user",
} as const;

export const AUDIT_PAGE_COPY = {
  searchAuditLog: "Search audit log...",
  auditSearch: "Audit search",
  changeHistory: "Change History",
  loadingAuditData: "Loading audit data...",
  errorLoadingAuditDataPleaseTry: "Error loading audit data. Please try again.",
  noChangeHistoryAvailableYet: "No change history available yet.",
  changesWillAppearHereWhenInventory: "Changes will appear here when inventory items are added, modified, or deleted.",
} as const;

export const AUTH_PAGE_COPY = {
  connectSidExpiresThu01Jan: "connect.sid=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;",
  userNotFound: "User not found",
  noUserFoundWithThisEmail: "No user found with this email address.",
  ifAnAccountExistsForAn: (resetEmail: string | number) => `If an account exists for ${resetEmail}, an email with instructions to reset your password has been sent. Please check your inbox (and spam folder).`,
  anUnexpectedErrorOccurred: "An unexpected error occurred.",
  failedToSendPasswordResetEmail: (errorMessage: string | number) => `Failed to send password reset email: ${errorMessage}`,
  unknownError: "Unknown error",
  chemicalInventoryManagementSystem: "Chemical Inventory Management System",
  enterYourCredentialsToAccessYour: "Enter your credentials to access your account",
  enterYourEmail: "Enter your email",
  enterYourPassword: "Enter your password",
  loggingIn: "Logging in...",
  forgotPassword: "Forgot Password?",
  resetPassword: "Reset Password",
  enterYourEmailAddressBelowWe: "Enter your email address below. We'll send you a link to reset your password.",
  sendResetLink: "Send Reset Link",
  trackYourChemicalInventoryWithEase: "Track your chemical inventory with ease. Keep detailed records of all chemicals in your facility and monitor changes over time.",
  trackChemicalDetailsIncludingProductName: "Track chemical details including product #, name, CAS, and batch numbers",
  monitorInventoryAmountsAndLocations: "Monitor inventory amounts and locations",
  detailedChangeHistoryForEveryModification: "Detailed change history for every modification",
  secureUserAuthenticationAndAccessControl: "Secure user authentication and access control",
} as const;

export const CONTACTS_PAGE_COPY = {
  filterAll: "All",
  filterSuppliers: "Suppliers",
  filterPurchasers: "Purchasers",
  filterBoth: "Both",
  youMustBeLoggedInTo: "You must be logged in to create a contact",
  companyCreatedSuccessfully: "Company created successfully",
  noContactSelectedForUpdate: "No contact selected for update",
  companyUpdatedSuccessfully: "Company updated successfully",
  companyDeletedSuccessfully: "Company deleted successfully",
  searchContacts: "Search contacts...",
  addCompany: "Add Company",
  loadingContacts: "Loading contacts...",
} as const;

export const INVENTORY_PAGE_COPY = {
  notSet: "Not set",
  searchInventory: "Search inventory...",
  allLocations: "All Locations",
  addChemical: "Add Chemical",
  loadingInventoryData: "Loading inventory data...",
  errorLoadingInventoryData: "Error loading inventory data",
  pleaseTryRefreshingThePage: "Please try refreshing the page",
  editChemical: "Edit Chemical",
  addNewChemical: "Add New Chemical",
  deleteDocument: "Delete document?",
  thisWillPermanentlyRemoveThePdf: (label: string | number) => `This will permanently remove the ${label} PDF for this batch. This cannot be undone.`,
  permanentlyDeleteThisVersion: "Permanently delete this version?",
  thisWillPermanentlyDeleteThisVersion: (label: string | number) => `This will permanently delete this ${label} version. Unlike clearing the current slot, this cannot be recovered.`,
} as const;

export const NOT_FOUND_COPY = {
  t404PageNotFound: "404 Page Not Found",
  didYouForgetToAddThe: "Did you forget to add the page to the router?",
} as const;

export const ORDERS_PAGE_COPY = {
  searchOrders: "Search orders...",
} as const;


export const TRANSACTIONS_PAGE_COPY = {
  searchTransactions: "Search transactions...",
  allTypes: "All types",
  recordPriorTransaction: "Record prior transaction",
  loadingTransactions: "Loading transactions...",
  recordPriorTransaction2: "Record Prior Transaction",
} as const;

export const UPDATE_PASSWORD_PAGE_COPY = {
  passwordsDoNotMatch: "Passwords do not match.",
  passwordMustBeAtLeast6: "Password must be at least 6 characters long.",
  passwordUpdatedSuccessfullyYouCanNow: "Password updated successfully! You can now log in with your new password.",
  passwordUpdatedSuccessfully: "Password updated successfully.",
  failedToUpdatePassword: "Failed to update password.",
  setNewPassword: "Set New Password",
  enterAndConfirmYourNewPassword: "Enter and confirm your new password below.",
  newPassword: "New Password",
  confirmNewPassword: "Confirm New Password",
  updatePassword: "Update Password",
  goToLogin: "Go to Login",
} as const;

export const USERS_PAGE_COPY = {
  couldNotGetYourEmailAddress: "Could not get your email address to send reset link.",
  anEmailWithInstructionsHasBeen: (email: string | number) => `An email with instructions has been sent to ${email}. Please check your inbox.`,
  editPermissions: "Edit Permissions",
  deleteUser: "Delete User",
  systemUsers: "System Users",
  myUserProfile: "My User Profile",
  loadingUsers: "Loading users...",
  errorLoadingUsersPleaseTryAgain: "Error loading users. Please try again.",
  searchUsers: "Search users...",
  noUsersFound: "No users found.",
  accountActions: "Account Actions",
  sendPasswordResetEmail: "Send Password Reset Email",
  clickThisButtonToReceiveAn: "Click this button to receive an email with a link to reset your password.",
} as const;
